import React, { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { CheckCircle, X, Sparkles } from 'lucide-react'
import { useProjectStore } from '../stores/projectStore'
import { getAnalysisStatus } from '../lib/api'
import { nanoid } from 'nanoid'

const POLL_INTERVAL_MS = 3000 // AnalyzingPage が離脱後は3秒ポーリング
const TOAST_DISMISS_MS = 10000 // 10秒で自動消去

/**
 * グローバルバックグラウンド分析ポーラー + 完了トースト
 * AppLayout に配置。AnalyzingPage が離脱後に分析完了を検知してトーストを表示する。
 */
export const AnalysisCompleteToast: React.FC = () => {
  const navigate = useNavigate()
  const {
    activeBackgroundAnalysis,
    isAnalyzingPageMounted,
    analysisToasts,
    setActiveBackgroundAnalysis,
    addAnalysisToast,
    dismissAnalysisToast,
  } = useProjectStore()

  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const isPollingRef = useRef(false)

  // バックグラウンドポーリング: AnalyzingPage が離脱しており、かつ分析が進行中の場合のみ動く
  useEffect(() => {
    const shouldPoll = activeBackgroundAnalysis !== null && !isAnalyzingPageMounted

    if (!shouldPoll) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      return
    }

    const { projectId, projectName } = activeBackgroundAnalysis

    const poll = async () => {
      if (isPollingRef.current) return
      isPollingRef.current = true
      try {
        const result = await getAnalysisStatus(projectId)
        if (!result.success || !result.data) return

        if (result.data.status === 'completed' && result.data.hasAssetPack) {
          // 完了: ポーリング停止 → トースト追加
          if (intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
          }
          setActiveBackgroundAnalysis(null)
          addAnalysisToast({
            id: nanoid(),
            projectId,
            projectName,
            createdAt: Date.now(),
          })
        } else if (result.data.status === 'error') {
          // エラー: ポーリング停止・トーストなし
          if (intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
          }
          setActiveBackgroundAnalysis(null)
        }
      } catch {
        // ネットワークエラーは無視してポーリング継続
      } finally {
        isPollingRef.current = false
      }
    }

    intervalRef.current = setInterval(poll, POLL_INTERVAL_MS)
    poll() // 即座に1回実行

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [activeBackgroundAnalysis, isAnalyzingPageMounted, setActiveBackgroundAnalysis, addAnalysisToast])

  // 自動削除タイマー
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())
  useEffect(() => {
    const currentIds = new Set(analysisToasts.map(t => t.id))
    for (const [id, timer] of timersRef.current) {
      if (!currentIds.has(id)) {
        clearTimeout(timer)
        timersRef.current.delete(id)
      }
    }
    for (const toast of analysisToasts) {
      if (!timersRef.current.has(toast.id)) {
        const timer = setTimeout(() => dismissAnalysisToast(toast.id), TOAST_DISMISS_MS)
        timersRef.current.set(toast.id, timer)
      }
    }
  }, [analysisToasts, dismissAnalysisToast])

  if (analysisToasts.length === 0) return null

  return (
    <div
      className="fixed z-120 flex flex-col gap-2 w-80"
      style={{ top: 80, right: 16 }}
    >
      {analysisToasts.map(toast => (
        <div
          key={toast.id}
          className="flex items-start gap-3 px-4 py-3 bg-surface-2 backdrop-blur-xs border border-ok rounded-xl shadow-xl shadow-black/40 cursor-pointer hover:border-ok transition-colors"
          onClick={() => {
            dismissAnalysisToast(toast.id)
            navigate(`/project/${toast.projectId}/template`)
          }}
        >
          {/* アイコン */}
          <div className="shrink-0 w-9 h-9 rounded-full bg-ok-bg flex items-center justify-center mt-0.5">
            <Sparkles className="w-4 h-4 text-ok" />
          </div>

          {/* テキスト */}
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-ok flex items-center gap-1">
              <CheckCircle className="w-3 h-3" />
              分析完了
            </p>
            <p className="text-sm font-semibold text-white mt-0.5 truncate">
              {toast.projectName}
            </p>
            <p className="text-[10px] text-ink-muted mt-0.5">
              クリックして素材を確認
            </p>
          </div>

          {/* 閉じるボタン */}
          <button
            onClick={(e) => {
              e.stopPropagation()
              dismissAnalysisToast(toast.id)
            }}
            className="shrink-0 text-ink-muted hover:text-ink transition-colors mt-0.5"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
    </div>
  )
}
